import { useState, useEffect } from "react";
import { useParams } from 'react-router-dom';
import axios from 'api/axiosInstance';
import endpoints from '../api/endpoints';
import { getSiteConfig } from "./siteConfig";
import scrollToSection from "utils/scrollToSection";
import styles from "./AppoinmentStyle.module.css";
import TemplateClean from "./TemplateClean";
import TemplateWellness from "./TemplateWellness";
import TemplateImpact from "./TemplateImpact";
import TemplateModern from "./TemplateModern";

const TEMPLATES = {
  clean: TemplateClean,
  wellness: TemplateWellness,
  impact: TemplateImpact,
  modern: TemplateModern,
  // legacy aliases — keep so old saved values still work
  dark: TemplateClean,
  'wellness-dark': TemplateWellness,
};

export default function AppoinmentTest({ slugOverride }) {
  const params = useParams();
  const slug = slugOverride || params.slug;
  const [companyData, setCompanyData] = useState(null);
  const [siteContentJson, setSiteContentJson] = useState(null);
  const [dbSections, setDbSections] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!slug) return;
    const fetchCompanyData = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('token');
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const response = await axios.get(endpoints.company.getCompany(slug), { headers });
        setCompanyData(response.data);

        if (response.data.company_id) {
          try {
            const sectionsRes = await axios.get(
              endpoints.company.websiteSections(response.data.company_id),
              { headers }
            );
            setDbSections(sectionsRes.data);
          } catch { /* no sections */ }
        }

        if (response.data.site_content_doc) {
          try {
            const docRes = await axios.get(response.data.site_content_doc);
            setSiteContentJson(docRes.data);
          } catch { /* no doc */ }
        }
      } catch (error) {
        console.error('Error fetching company data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchCompanyData();
  }, [slug]);

  const config = getSiteConfig(companyData, siteContentJson, dbSections);

  // Canonical custom domain: if this company has a custom domain and we're viewing
  // the booking page from a different host (e.g. the /appointment/<slug> route on
  // the main site), forward to the custom domain so it's the canonical public URL.
  useEffect(() => {
    const cd = (companyData?.custom_domain || '').trim().toLowerCase();
    if (!cd) return;
    const host = window.location.hostname.toLowerCase().replace(/^www\./, '');
    const target = cd.replace(/^www\./, '');
    if (host === target) return;                       // already on the custom domain
    if (['localhost', '127.0.0.1'].includes(host)) return; // don't hijack local dev
    window.location.replace(`https://${cd}/${window.location.hash || ''}`);
  }, [companyData?.custom_domain]);

  // Tab title reflects the client's site (company name); falls back to ZoidesQ.
  useEffect(() => {
    if (companyData?.company_name) {
      document.title = companyData.company_name;
    }
    return () => { document.title = 'ZoidesQ'; };
  }, [companyData?.company_name]);

  // Universal in-page link scrolling: if the URL carries a #hash (from a nav/footer
  // anchor), smooth-scroll to that section. Covers any link the templates miss.
  useEffect(() => {
    const scrollToHash = () => {
      const id = decodeURIComponent(window.location.hash.replace('#', '')).trim();
      if (!id || !document.getElementById(id)) return;
      scrollToSection(id);
    };
    window.addEventListener('hashchange', scrollToHash);
    const t = setTimeout(scrollToHash, 600); // after first content paint
    return () => { window.removeEventListener('hashchange', scrollToHash); clearTimeout(t); };
  }, []);

  if (loading) {
    return (
      <div className={styles.container}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', gap: '16px' }}>
          <div style={{ width: '48px', height: '48px', border: '4px solid #f3f3f3', borderTop: `4px solid ${config.accentColor}`, borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
          <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
          <p style={{ color: '#666', fontSize: '16px' }}>Loading...</p>
        </div>
      </div>
    );
  }

  const templateKey = companyData?.site_template || 'clean';
  const TemplateComponent = TEMPLATES[templateKey] || TemplateClean;

  return <TemplateComponent config={config} companyData={companyData} />;
}
